import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {
  DataFrameResolutionConfig,
  DataFrameValueResolutionMode,
} from 'data-frames/util/resolveDataFrameValue';
import {
  AnalyticsVisualizationAggregationType as AggregationType,
  AnalyticsVisualizationLegendLocation,
  AnalyticsVisualizationAxisScale as InsightsAxisScale,
  AnalyticsVisualizationColorPalette as ColorPalette,
  AnalyticsVisualizationErrorBarType as ErrorBarType,
  AnalyticsVisualizationSortDirection,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {LegendLocation} from 'ui-library/charting/internals/LegendConstants';
import {AxisScale as ChartingAxisScale} from 'ui-library/charting/Types';
import {BenchlingDuotoneIconName} from 'ui-library/icons/BenchlingDuotoneIcon';

import {AnalyticsVisualization} from './__generated/AnalyticsVisualization';
import {VisualizationType, VisualizationTypeToParameters} from './AnalyticsVisualizationTypes';

type AnalyticsVisualizationParameters = AnalyticsVisualization['parameters'];

const ANALYTICS_VISUALIZATION_FRAGMENT: DocumentNode = gql`
  fragment AnalyticsVisualization on AnalyticsVisualizationNode {
    id
    parameters {
      ... on AnalyticsVisualizationBarChartParametersNode {
        aggregationType
        barChartType
        errorBarType
        seriesColumnName
        sortDirection
        xAxisColumnName
        yAxisColumnName
        yAxisMin
        yAxisMax
        yAxisScale
      }
      ... on AnalyticsVisualizationHeatmapParametersNode {
        gradientDirection
        metricColumnName
        xAxisColumnName
        yAxisColumnName
      }
      ... on AnalyticsVisualizationLineChartParametersNode {
        seriesColumnName
        xAxisColumnName
        xAxisScale
        yAxisColumnName
        yAxisIncludesZero
        yAxisScale
      }
      ... on AnalyticsVisualizationScatterPlotParametersNode {
        aggregationType
        colorPalette
        errorBarType
        legendLocation
        outlierConfig {
          outlierSetting
          qValue
        }
        regressionConfig
        rowIdColumnName
        seriesColumnName
        xAxisColumnName
        xAxisScale
        yAxisColumnName
        yAxisIncludesZero
        yAxisMin
        yAxisMax
        yAxisScale
      }
      ... on AnalyticsVisualizationTableParametersNode {
        displayColumnsJson
      }
      ... on AnalyticsVisualizationPieChartParametersNode {
        aggregationType
        legendLocation
        measureColumnName
        showPercentageInLabel
        showSliceNameInLabel
        showValueInLabel
        sliceColumnName
      }
    }
  }
`;

export const AnalyticsVisualizationFragment: FragmentWrapper<AnalyticsVisualization> = {
  fragment: ANALYTICS_VISUALIZATION_FRAGMENT,
  fragmentName: 'AnalyticsVisualization',
};

export const VISUALIZATION_TYPE_TO_READABLE: {[key in VisualizationType]: string} = {
  [VisualizationType.BAR_CHART]: 'Bar chart',
  [VisualizationType.HEATMAP]: 'Heatmap',
  [VisualizationType.LINE_CHART]: 'Line chart',
  [VisualizationType.PIE_CHART]: 'Pie chart',
  [VisualizationType.SCATTER_PLOT]: 'Scatter plot',
  [VisualizationType.TABLE]: 'Table',
};

export const VISUALIZATION_TYPE_TO_ICON: {[key in VisualizationType]: BenchlingDuotoneIconName} = {
  [VisualizationType.BAR_CHART]: 'analyze',
  [VisualizationType.HEATMAP]: 'heatmap',
  [VisualizationType.LINE_CHART]: 'line-chart',
  [VisualizationType.PIE_CHART]: 'pie-chart',
  [VisualizationType.SCATTER_PLOT]: 'scatter-plot',
  [VisualizationType.TABLE]: 'table',
};

export type ParametersTypename = AnalyticsVisualizationParameters['__typename'];

export const PARAMETERS_TYPENAME_TO_VISUALIZATION_TYPE: {[k in ParametersTypename]: VisualizationType} = {
  AnalyticsVisualizationBarChartParametersNode: VisualizationType.BAR_CHART,
  AnalyticsVisualizationHeatmapParametersNode: VisualizationType.HEATMAP,
  AnalyticsVisualizationLineChartParametersNode: VisualizationType.LINE_CHART,
  AnalyticsVisualizationPieChartParametersNode: VisualizationType.PIE_CHART,
  AnalyticsVisualizationScatterPlotParametersNode: VisualizationType.SCATTER_PLOT,
  AnalyticsVisualizationTableParametersNode: VisualizationType.TABLE,
};

export const VISUALIZATION_TYPE_TO_PARAMETERS_TYPENAME: {[k in VisualizationType]: ParametersTypename} = {
  [VisualizationType.BAR_CHART]: 'AnalyticsVisualizationBarChartParametersNode',
  [VisualizationType.HEATMAP]: 'AnalyticsVisualizationHeatmapParametersNode',
  [VisualizationType.LINE_CHART]: 'AnalyticsVisualizationLineChartParametersNode',
  [VisualizationType.PIE_CHART]: 'AnalyticsVisualizationPieChartParametersNode',
  [VisualizationType.SCATTER_PLOT]: 'AnalyticsVisualizationScatterPlotParametersNode',
  [VisualizationType.TABLE]: 'AnalyticsVisualizationTableParametersNode',
};

// This guard lives here rather than next to the types it narrows because it reads
// VISUALIZATION_TYPE_TO_PARAMETERS_TYPENAME. Importing this module from AnalyticsVisualizationTypes would
// create an import cycle where, depending on which module a bundle evaluates first, the maps above are built
// before VisualizationType is initialized.
export function isVisualizationTypeParameters<TVisualizationType extends VisualizationType>(
  parameters: AnalyticsVisualizationParameters,
  visualizationType: TVisualizationType
): parameters is VisualizationTypeToParameters[TVisualizationType] {
  return VISUALIZATION_TYPE_TO_PARAMETERS_TYPENAME[visualizationType] === parameters.__typename;
}

// Visualizations that are set as "flex grow" are visualizations whose CSS height is defined as flex-grow, which
// means they vertically grow to fit their parent div. Other visualizations, like tables for example, define
// their own height, and they only grow relative to their own contents as opposed to their parent div's height.
export const VISUALIZATION_TYPE_TO_FLEX_GROW: {[k in VisualizationType]: boolean} = {
  [VisualizationType.BAR_CHART]: true,
  [VisualizationType.HEATMAP]: true,
  [VisualizationType.LINE_CHART]: true,
  [VisualizationType.PIE_CHART]: true,
  [VisualizationType.SCATTER_PLOT]: true,
  [VisualizationType.TABLE]: false,
};

// Whether to render the chart such that the edges of its component touch the edges of the block or render it
// with some padding. This should generally be set to true for Visx and Chart.js charts.
export const VISUALIZATION_TYPE_TO_NEEDS_PADDING: {[k in VisualizationType]: boolean} = {
  [VisualizationType.BAR_CHART]: true,
  [VisualizationType.HEATMAP]: true,
  [VisualizationType.LINE_CHART]: true,
  [VisualizationType.PIE_CHART]: true,
  [VisualizationType.SCATTER_PLOT]: true,
  [VisualizationType.TABLE]: false,
};

export const INSIGHTS_LEGEND_LOCATION_TO_CHARTING_ENUM: {
  [key in AnalyticsVisualizationLegendLocation]: LegendLocation;
} = {
  [AnalyticsVisualizationLegendLocation.LEFT]: LegendLocation.LEFT,
  [AnalyticsVisualizationLegendLocation.RIGHT]: LegendLocation.RIGHT,
  [AnalyticsVisualizationLegendLocation.TOP]: LegendLocation.TOP,
};

export const INSIGHTS_SCALE_TYPE_TO_CHARTING_SCALE_TYPE: {[k in InsightsAxisScale]: ChartingAxisScale} = {
  [InsightsAxisScale.LINEAR]: ChartingAxisScale.LINEAR,
  [InsightsAxisScale.LOG]: ChartingAxisScale.LOG,
};

export const AGGREGATION_TYPE_TO_READABLE: {[key in AggregationType]: string} = {
  [AggregationType.COUNT]: 'Count',
  [AggregationType.MEAN]: 'Mean',
  [AggregationType.SUM]: 'Sum',
};

export const ERROR_BAR_TYPE_TO_READABLE: {[key in ErrorBarType]: string} = {
  [ErrorBarType.STANDARD_DEVIATION]: 'Standard deviation',
  [ErrorBarType.STANDARD_ERROR]: 'Standard error',
};

export const LEGEND_LOCATION_TO_READABLE: {[key in AnalyticsVisualizationLegendLocation]: string} = {
  [AnalyticsVisualizationLegendLocation.LEFT]: 'Left',
  [AnalyticsVisualizationLegendLocation.RIGHT]: 'Right',
  [AnalyticsVisualizationLegendLocation.TOP]: 'Top',
};

// Keep in sync with benchling/insights/models/analytics_visualizations/analytics_visualization_parameters.py:AnalyticsVisualizationScatterPlotParameters.DEFAULT_COLOR_PALETTE
export const SCATTER_PLOT_DEFAULT_COLOR_PALETTE = ColorPalette.DISCRETE;

export const VISUALIZATION_RESOLUTION_CONFIG: DataFrameResolutionConfig = {
  apiIdentifiedObjects: DataFrameValueResolutionMode.NAME,
  blobs: DataFrameValueResolutionMode.WAREHOUSE_BLOB_DICT,
};

export const SORT_DIRECTION_TO_READABLE: {[key in AnalyticsVisualizationSortDirection]: string} = {
  [AnalyticsVisualizationSortDirection.ASCENDING]: 'Ascending',
  [AnalyticsVisualizationSortDirection.DESCENDING]: 'Descending',
};
